import { useState, useCallback, useRef, useMemo, useEffect } from 'react';

export interface UseAudioRecordingOptions {
  maxDurationMs?: number; // Maximum recording duration in milliseconds
  mimeType?: string; // Audio MIME type to use
}

export interface UseAudioRecordingResult {
  isRecording: boolean;
  isPaused: boolean;
  audioBlob: Blob | null;
  audioUrl: string | null;
  duration: number;
  status: 'idle' | 'recording' | 'paused' | 'stopped' | 'error';
  stream: MediaStream | null;
  error: Error | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
  warmupCompleted: boolean;
}

/**
 * Hook for audio recording functionality with support for pause/resume
 * 
 * @param deviceId Optional device ID for the microphone to use
 * @param options Recording options (maxDuration, mimeType)
 * @returns Recording controls and state
 */
export function useAudioRecording(
  deviceId?: string,
  options: UseAudioRecordingOptions = {}
): UseAudioRecordingResult {
  const { maxDurationMs = 10 * 60 * 1000, mimeType = 'audio/webm' } = options;
  
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [status, setStatus] = useState<'idle' | 'recording' | 'paused' | 'stopped' | 'error'>('idle');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [warmupCompleted, setWarmupCompleted] = useState(false);

  // References for internal state
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number | null>(null);
  const pausedDurationRef = useRef<number>(0);
  const timeoutRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  // Determine the supported MIME type for this browser
  const supportedMimeType = useMemo(() => {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
    
    // Try fallback MIME types
    const fallbackTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
    for (const type of fallbackTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    
    // Last resort - use browser default
    return '';
  }, [mimeType]);

  // Clean up any active stream and recorders
  const cleanup = useCallback(() => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (err) {
          console.warn('Error stopping MediaRecorder:', err);
        }
      }
      mediaRecorderRef.current = null;
    }
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [stream]);

  // Forward declaration to resolve circular dependency
  const stopRecordingRef = useRef<() => Promise<Blob>>(undefined);

  // Stop recording implementation
  const stopRecording = useCallback(async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        cleanup();
        reject(new Error('Not recording'));
        return;
      }
      
      // Set up a handler for the stop event
      const onStopHandler = () => {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.removeEventListener('stop', onStopHandler);
        }
        
        if (audioChunksRef.current.length === 0) {
          cleanup();
          reject(new Error('No audio data recorded'));
          return;
        }
        
        const audioType = supportedMimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: audioType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setIsRecording(false);
        setIsPaused(false);
        setWarmupCompleted(false);
        setStatus('stopped');
        
        cleanup();
        resolve(blob);
      };
      
      // Add the event listener
      mediaRecorderRef.current.addEventListener('stop', onStopHandler);
      
      // Stop the recorder
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        cleanup();
        reject(err);
      }
    });
  }, [cleanup, supportedMimeType]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      // Reset state
      setError(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setDuration(0);
      audioChunksRef.current = [];
      pausedDurationRef.current = 0;
      startTimeRef.current = null;
      setWarmupCompleted(false);
      
      // Request microphone access
      const constraints: MediaStreamConstraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false
      };
      
      const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(audioStream);
      
      // Create the MediaRecorder
      const recorder = new MediaRecorder(audioStream, {
        mimeType: supportedMimeType
      });
      
      // Set up event handlers
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          setStatus('idle');
          setWarmupCompleted(false);
          return;
        }
        
        // Create the final audio blob
        const audioType = supportedMimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: audioType });
        
        // Create URL for playback
        const url = URL.createObjectURL(blob);
        
        setAudioBlob(blob);
        setAudioUrl(url);
        setWarmupCompleted(false);
        setStatus('stopped');
      };
      
      recorder.onerror = (event) => {
        setError(new Error(`Recording error: ${event.error}`));
        setStatus('error');
        cleanup();
      };
      
      // Start recording with a short delay to allow microphone warm-up
      // This helps prevent cutting off the first few words
      mediaRecorderRef.current = recorder;
      
      // Set state to recording but add a slight delay before actually starting
      setIsRecording(true);
      setIsPaused(false);
      setStatus('recording');
      
      // Add a 750ms delay before starting recording to allow the microphone to initialize
      // This helps capture the first words spoken without cutting them off
      setTimeout(() => {
        if (mediaRecorderRef.current) {
          recorder.start(1000); // Collect data in 1-second chunks
          startTimeRef.current = Date.now();
          setWarmupCompleted(true);
          console.log("Recording started after warm-up delay");
        }
      }, 750);
      
      // Set up duration tracking
      intervalRef.current = window.setInterval(() => {
        if (startTimeRef.current && !isPaused) {
          const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
          setDuration(elapsed);
        }
      }, 100);
      
      // Set up automatic stop after maxDuration
      if (maxDurationMs > 0) {
        timeoutRef.current = window.setTimeout(() => {
          if (mediaRecorderRef.current?.state === 'recording') {
            // Use the ref to avoid circular dependency
            stopRecordingRef.current?.().catch(console.error);
          }
        }, maxDurationMs);
      }
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setStatus('error');
      cleanup();
    }
  }, [deviceId, supportedMimeType, maxDurationMs, isPaused, cleanup]);

  // Update the ref after definition
  stopRecordingRef.current = stopRecording;

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      setStatus('paused');
      
      // Store the current paused time to calculate total pause duration
      pausedDurationRef.current += Date.now() - (startTimeRef.current || 0);
      startTimeRef.current = Date.now();
    }
  }, []);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      setStatus('recording');
      
      // Update start time for duration calculation
      startTimeRef.current = Date.now();
    }
  }, []);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    cleanup();
    setIsRecording(false);
    setIsPaused(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    setStatus('idle');
    setWarmupCompleted(false);
    audioChunksRef.current = [];
  }, [cleanup]);

  // The warmupCompleted state has been moved to the top with other states
  
  // When recording starts, reset warmupCompleted
  useEffect(() => {
    if (isRecording) {
      if (startTimeRef.current === null) {
        setWarmupCompleted(false);
      } else {
        setWarmupCompleted(true);
      }
    }
  }, [isRecording]);

  return {
    isRecording,
    isPaused,
    audioBlob,
    audioUrl,
    duration,
    status,
    stream,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    warmupCompleted
  };
}